// Apple IAP glue — wraps capacitor-plugin-cdv-purchase (StoreKit 2).
// The plugin's JS must be imported for window.CdvPurchase/store to exist;
// we lazy-import it only inside the native iOS shell. Safe no-op in browsers.
import { api } from "@/lib/api";
import { isNativeIOS } from "@/lib/nativePush";

export const IAP_UNAVAILABLE_MSG =
  "Apple purchases are temporarily unavailable. Please try again later.";

export const IAP_PRODUCT_IDS = {
  coins_500: "Com.mypandax.app.coins_500",
  coins_2000: "Com.mypandax.app.coins_2000",
  coins_10000: "com.mypandax.app.coins_10000",
  panda_plus_monthly: "com.mypandax.app.panda_plus_monthly",
};

let mod = null;
let registered = false;
let initPromise = null;
let lastIapError = null;

export function getIapLastError() {
  return lastIapError;
}

export function iapAvailable() {
  return isNativeIOS();
}

const withTimeout = (p, ms) =>
  Promise.race([
    p,
    new Promise((_, reject) => setTimeout(() => reject(new Error("StoreKit init timed out")), ms)),
  ]);

export function initIap() {
  if (!isNativeIOS()) return Promise.resolve(false);
  if (initPromise) return initPromise;
  initPromise = withTimeout(
    (async () => {
      mod = await import("capacitor-plugin-cdv-purchase");
      const { store, ProductType, Platform } = mod;
      if (!registered) {
        registered = true;
        store.register([
          { id: IAP_PRODUCT_IDS.coins_500, type: ProductType.CONSUMABLE, platform: Platform.APPLE_APPSTORE },
          { id: IAP_PRODUCT_IDS.coins_2000, type: ProductType.CONSUMABLE, platform: Platform.APPLE_APPSTORE },
          { id: IAP_PRODUCT_IDS.coins_10000, type: ProductType.CONSUMABLE, platform: Platform.APPLE_APPSTORE },
          { id: IAP_PRODUCT_IDS.panda_plus_monthly, type: ProductType.PAID_SUBSCRIPTION, platform: Platform.APPLE_APPSTORE },
        ]);
        store.when().approved(async (transaction) => {
          // Server is the source of truth — send the signed JWS, finish only on ack.
          // SK2 mode: the JWS lives directly on the transaction (jwsRepresentation).
          const jws = transaction?.jwsRepresentation
            || transaction?.transactionReceipt
            || transaction?.nativePurchase?.jwsRepresentation
            || transaction?.parentReceipt?.nativeData?.appStoreReceipt;
          if (!jws || jws.length < 100) {
            lastIapError = "Signed receipt missing from StoreKit transaction — left open for retry on next launch.";
            console.warn("[iap]", lastIapError);
            window.dispatchEvent(new CustomEvent("iap:error", { detail: { message: lastIapError } }));
            return; // never finish an uncredited purchase
          }
          try {
            const res = await api.post("/iap/verify", { jws });
            transaction.finish();
            window.dispatchEvent(new CustomEvent("iap:credited", { detail: { credited: res?.data?.credited ?? null } }));
          } catch (e) {
            lastIapError = `Server verify failed (${e?.response?.status || "network"}): ${e?.response?.data?.detail || e?.message}`;
            console.warn("[iap] leaving transaction open:", lastIapError);
            window.dispatchEvent(new CustomEvent("iap:error", { detail: { message: lastIapError } }));
          }
        });
      }
      const errors = await store.initialize([Platform.APPLE_APPSTORE]);
      if (Array.isArray(errors) && errors.length) {
        throw new Error(errors[0]?.message || "StoreKit failed to initialize.");
      }
      // "Ready" means StoreKit actually returned purchasable products.
      const hasOffer = () =>
        Object.values(IAP_PRODUCT_IDS).some((id) => {
          const p = store.get(id, Platform.APPLE_APPSTORE) || store.get(id);
          return !!(p && p.getOffer && p.getOffer());
        });
      const deadline = Date.now() + 15000;
      while (!hasOffer()) {
        if (Date.now() > deadline) throw new Error("StoreKit products did not load.");
        await new Promise((r) => setTimeout(r, 500));
      }
      return true;
    })(),
    30000
  ).catch((e) => {
    lastIapError = e?.message || String(e);
    console.warn("[iap] init failed:", lastIapError);
    initPromise = null;
    return false;
  });
  return initPromise;
}

export function getIapProduct(productId) {
  if (!mod) return null;
  return mod.store.get(productId, mod.Platform.APPLE_APPSTORE) || mod.store.get(productId);
}

export async function buyIap(productId) {
  const ready = await initIap();
  if (!ready) throw new Error(IAP_UNAVAILABLE_MSG);
  const product = getIapProduct(productId);
  const offer = product?.getOffer?.();
  if (!offer) throw new Error("This product isn't available right now.");
  const err = await mod.store.order(offer);
  if (err) throw new Error(err.message || "Purchase failed.");
}

export async function restoreIap() {
  const ready = await initIap();
  if (!ready) throw new Error(IAP_UNAVAILABLE_MSG);
  await mod.store.restorePurchases();
}
